/**
 * Home treatments carousel (the mini-card rail).
 *
 * - Auto-scrolls continuously (marquee) with a seamless loop.
 * - Can be dragged with the pointer (grab/grabbing).
 * - Pauses on hover and while dragging.
 * - Respects prefers-reduced-motion (no auto-scroll, drag still works).
 *
 * @param {Document} root
 */
const SPEED = 1.3; // pixels per frame

export function initTreatmentsCarousel(root = document) {
  const rail = root.querySelector('.treatments-section__rail');

  if (!rail || rail.dataset.carouselInitialized === 'true') {
    return;
  }

  const originals = Array.from(rail.children);

  if (originals.length === 0) {
    return;
  }

  rail.dataset.carouselInitialized = 'true';

  // Prevent the browser's native image/element ghost-drag so pointer drag
  // scrolls the rail instead of dragging the picture.
  rail.addEventListener('dragstart', (event) => event.preventDefault());

  // Clone the full set once so the loop is seamless.
  originals.forEach((node) => {
    const clone = node.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.dataset.clone = 'true';
    rail.appendChild(clone);
  });

  // Width of one full set (origin of first clone - origin of first item).
  const getBlockWidth = () => {
    const firstClone = rail.querySelector('[data-clone="true"]');

    if (!firstClone) {
      return 0;
    }

    return firstClone.offsetLeft - originals[0].offsetLeft;
  };

  let blockWidth = getBlockWidth();

  const wrap = () => {
    if (blockWidth <= 0) {
      return;
    }

    if (rail.scrollLeft >= blockWidth) {
      rail.scrollLeft -= blockWidth;
    } else if (rail.scrollLeft <= 0) {
      rail.scrollLeft += blockWidth;
    }
  };

  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  let paused = reduceMotion;

  const tick = () => {
    if (!paused) {
      rail.scrollLeft += SPEED;
      wrap();
    }

    window.requestAnimationFrame(tick);
  };

  if (!reduceMotion) {
    window.requestAnimationFrame(tick);
  }

  // Pause on hover.
  rail.addEventListener('pointerenter', () => {
    paused = true;
  });

  rail.addEventListener('pointerleave', () => {
    if (!rail.classList.contains('is-dragging')) {
      paused = reduceMotion ? true : false;
    }
  });

  // Drag to scroll.
  let isDown = false;
  let startX = 0;
  let startScroll = 0;

  rail.addEventListener('pointerdown', (event) => {
    isDown = true;
    paused = true;
    startX = event.clientX;
    startScroll = rail.scrollLeft;
    rail.classList.add('is-dragging');
    rail.setPointerCapture?.(event.pointerId);
  });

  rail.addEventListener('pointermove', (event) => {
    if (!isDown) {
      return;
    }

    event.preventDefault();
    rail.scrollLeft = startScroll - (event.clientX - startX);
    wrap();
  });

  const endDrag = (event) => {
    if (!isDown) {
      return;
    }

    isDown = false;
    rail.classList.remove('is-dragging');
    rail.releasePointerCapture?.(event.pointerId);
    paused = reduceMotion ? true : false;
  };

  rail.addEventListener('pointerup', endDrag);
  rail.addEventListener('pointercancel', endDrag);

  // Recompute the loop width when the layout changes.
  window.addEventListener('resize', () => {
    blockWidth = getBlockWidth();
  });
}
